import clsx from "clsx";
import { type ReactNode, useEffect, useRef } from "react";
import detectOutsideClick from "../utils/detectOutsideClick";
import toggleBodyScrolling from "../utils/toggleBodyScrolling";

export default function Popup({
  isOpen,
  closeCallback,
  children,
}: {
  isOpen: boolean;
  closeCallback: () => void;
  children: ReactNode;
}) {
  const contentContainer = useRef(null);
  const hasMounted = useRef(false);

  useEffect(() => {
    if (!hasMounted.current) {
      hasMounted.current = true;
    } else {
      toggleBodyScrolling(isOpen);
    }

    if (isOpen) {
      //We need the timeout so our initial Click won't close the component, so
      //we take animation duration before we add the listener
      setTimeout(() => {
        window.addEventListener("click", handleClick);
      }, 300);
    } else {
      window.removeEventListener("click", handleClick);
    }
    return () => {
      window.removeEventListener("click", handleClick);
    };
  }, [isOpen]);

  function handleClick(e: MouseEvent) {
    if (!contentContainer?.current) return;

    detectOutsideClick(e, contentContainer, closeCallback);
  }

  if (!isOpen) return null;

  return (
    <div
      className={clsx(
        "fixed left-0 top-0 z-50 h-full w-full transition-all",
        isOpen ? "opacity-100" : "pointer-events-none opacity-0",
      )}
    >
      <div className="relative flex h-full w-full items-center justify-center p-5 md:p-10 lg:p-20">
        <div className="absolute h-screen w-screen bg-black/60 transition-opacity"></div>
        <div
          className="w-full max-w-3xl bg-white drop-shadow-sm-white 2xl:max-w-5xl"
          ref={contentContainer}
        >
          {children}
        </div>
      </div>
    </div>
  );
}
